import { BOARD_TEXT_FONT, normalizeTextFont } from "./textSession";
import type { TextRun } from "@bezpart-board/shared";
import type { SceneObject } from "./SceneHost";
import { readObjectStyle, stickyBackground } from "../lib/board/sceneStyle";
import { objectRuns, runsToHtml, runsPatch, runsWithoutFontSize } from "./textRuns";
import { isFrameObject, frameTitle } from "./frame/frameContext";
import type { Bounds } from "./selectionBounds";
import { scaledFontSize, type ResizeHandleId } from "./transform/resize";

export function isTextObject(obj: SceneObject): obj is SceneObject & { type: "text"; w: number; h: number } {
  return obj.type === "text";
}

export function isStickyObject(
  obj: SceneObject
): obj is SceneObject & { type: "sticky"; w: number; h: number } {
  return obj.type === "sticky";
}

/** Текст / стикер — клик по уже выбранному открывает редактор. */
export function isTextLikeObject(obj: SceneObject): boolean {
  return isTextObject(obj) || isStickyObject(obj);
}

/** Двойной клик открывает overlay (включая заголовок рамки). */
export function isOverlayEditableObject(obj: SceneObject): boolean {
  return isTextLikeObject(obj) || isFrameObject(obj);
}

export function textContent(obj: SceneObject): string {
  if (isFrameObject(obj)) return frameTitle(obj);
  return typeof obj.text === "string" ? obj.text : "";
}

/** Безопасный href из meta (только http/https). */
export function objectHref(obj: SceneObject | null | undefined): string | null {
  if (!obj || !obj.meta || typeof obj.meta !== "object") return null;
  const raw = (obj.meta as Record<string, unknown>).href;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.href;
  } catch {
    return null;
  }
}

/**
 * Ссылка на куске текста под точкой экрана. Доску накрывает слой для мыши,
 * поэтому сам щелчок до <a> не доходит — ищем по расположению кусков.
 */
export function pieceLinkAtPoint(
  el: Element | null,
  clientX: number,
  clientY: number
): string | null {
  if (!el) return null;
  for (const a of Array.from(el.querySelectorAll("a[data-wb-link]"))) {
    for (const r of Array.from(a.getClientRects())) {
      if (
        clientX >= r.left - 2 &&
        clientX <= r.right + 2 &&
        clientY >= r.top - 2 &&
        clientY <= r.bottom + 2
      ) {
        const href = a.getAttribute("href");
        if (!href) return null;
        try {
          const u = new URL(href);
          return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

export function openLinkHref(href: string): void {
  window.open(href, "_blank", "noopener,noreferrer");
}

export function openObjectHref(obj: SceneObject | null | undefined): boolean {
  const href = objectHref(obj);
  if (!href) return false;
  window.open(href, "_blank", "noopener,noreferrer");
  return true;
}

/**
 * Фон надписи — плашка под всем текстом, как в Miro. Только из style.fill
 * новой схемы: у старых надписей поле fill бывало цветом букв.
 */
export function textFill(obj: SceneObject): string | undefined {
  if (!isTextObject(obj)) return undefined;
  const raw = (obj.style as { fill?: unknown } | undefined)?.fill;
  if (typeof raw !== "string") return undefined;
  const v = raw.trim();
  if (!v || v === "none" || v === "transparent") return undefined;
  return v;
}

export function textDisplayStyle(obj: SceneObject): {
  color: string;
  fontSize: number;
  fontFamily: string;
  textAlign: string;
  lineHeight: number;
  fontWeight?: "normal" | "bold";
  fontStyle?: "normal" | "italic";
  background?: string;
  textDecoration?: string;
} {
  const style = readObjectStyle(obj);
  if (isFrameObject(obj)) {
    return {
      color: "#475569",
      fontSize: 14,
      fontFamily: BOARD_TEXT_FONT,
      textAlign: "left",
      lineHeight: 1.2,
      background: "transparent",
    };
  }
  const href = objectHref(obj);
  const isLink = Boolean(href) && isTextObject(obj);
  const userDecoration =
    typeof style.textDecoration === "string" && style.textDecoration !== "none"
      ? style.textDecoration
      : "";
  const decorations = new Set(
    userDecoration
      .split(/\s+/)
      .map((d) => d.trim())
      .filter(Boolean)
  );
  if (isLink) decorations.add("underline");
  return {
    color: isLink ? "#2563eb" : style.color ?? "#1a1a2e",
    fontSize: style.fontSize ?? (isStickyObject(obj) ? 15 : 18),
    fontFamily: normalizeTextFont(style.fontFamily),
    textAlign: style.textAlign ?? "left",
    lineHeight: style.lineHeight ?? TEXT_LINE_HEIGHT,
    fontWeight: style.fontWeight === "bold" ? "bold" : "normal",
    fontStyle: style.fontStyle === "italic" ? "italic" : "normal",
    background: isStickyObject(obj) ? stickyBackground(obj) : textFill(obj),
    textDecoration: decorations.size ? [...decorations].join(" ") : undefined,
  };
}

export const TEXT_BOX_PAD_X = 6;
export const TEXT_BOX_PAD_Y = 4;
export const TEXT_BOX_MIN_W = 40;
export const TEXT_BOX_MIN_H = 28;
/** Межстрочный интервал надписи (и в показе, и в измерении). */
export const TEXT_LINE_HEIGHT = 1.35;
export const TEXT_BOX_MAX_W = 720;
export const TEXT_BOX_UNWRAP_MAX_W = 8192;
/** Запас к ширине: иначе после ceil(offsetWidth) последняя буква может уехать на следующую строку. */
export const TEXT_WRAP_SLACK_PX = 2;

/** Miro: auto — рамка по содержимому; fixed — ширина задана боковыми ручками. */
export type TextWidthMode = "auto" | "fixed";

/**
 * Ширина, которую зафиксировал первый Enter, может расти до самой длинной
 * строки (но не уже, чем была). Без этого пункт списка длиннее первой
 * строки рвался посередине — маркер на одной строке, слово на другой.
 * Ширину, заданную руками за бок рамки, не трогаем: там флаг снят.
 */
export function textWidthGrows(obj: SceneObject): boolean {
  if (!isTextObject(obj) || !obj.meta || typeof obj.meta !== "object") return false;
  return (obj.meta as Record<string, unknown>).textWidthGrow === true;
}

/** Ширина фиксированной надписи с учётом роста до самой длинной строки. */
export function grownTextWidth(
  obj: SceneObject,
  text: string,
  style: TextMeasureStyle,
  currentW: number,
  html?: string
): number {
  if (!textWidthGrows(obj)) return currentW;
  const natural = measureTextObjectSize(text || " ", style, TEXT_BOX_MAX_W, html).w;
  return Math.max(currentW, Math.min(TEXT_BOX_MAX_W, natural));
}

export function textWidthMode(obj: SceneObject): TextWidthMode {
  if (!isTextObject(obj) || !obj.meta || typeof obj.meta !== "object") return "auto";
  return (obj.meta as Record<string, unknown>).textWidthMode === "fixed" ? "fixed" : "auto";
}

export function mergeTextMeta(
  obj: SceneObject,
  partial: Record<string, unknown>
): Record<string, unknown> {
  const prev =
    obj.meta && typeof obj.meta === "object" ? (obj.meta as Record<string, unknown>) : {};
  return { ...prev, ...partial };
}

type TextMeasureStyle = {
  fontSize: number;
  fontFamily: string;
  lineHeight: number;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
};

let measureProbe: HTMLDivElement | null = null;

function getMeasureProbe(): HTMLDivElement | null {
  if (typeof document === "undefined") return null;
  if (!measureProbe) {
    measureProbe = document.createElement("div");
    measureProbe.setAttribute("aria-hidden", "true");
    measureProbe.style.cssText = [
      "position:absolute",
      "left:-99999px",
      "top:0",
      "visibility:hidden",
      "pointer-events:none",
      "box-sizing:border-box",
      "height:auto",
    ].join(";");
    document.body.appendChild(measureProbe);
  }
  return measureProbe;
}

function underlineExtra(style: TextMeasureStyle): number {
  return style.textDecoration && style.textDecoration !== "none" ? 4 : 2;
}

function applyProbeFont(probe: HTMLDivElement, style: TextMeasureStyle): void {
  const fontSize = style.fontSize > 0 ? style.fontSize : 18;
  const lineHeight = style.lineHeight > 0 ? style.lineHeight : 1.35;
  probe.style.fontSize = `${fontSize}px`;
  probe.style.fontFamily = normalizeTextFont(style.fontFamily);
  probe.style.fontWeight = style.fontWeight || "normal";
  probe.style.fontStyle = style.fontStyle || "normal";
  probe.style.textDecoration = style.textDecoration || "none";
  probe.style.lineHeight = String(lineHeight);
  probe.style.padding = `${TEXT_BOX_PAD_Y}px ${TEXT_BOX_PAD_X}px`;
}

function applyProbeWrap(probe: HTMLDivElement): void {
  // Как в overlay auto и на доске: перенос по пробелам, длинные слова — через overflow-wrap.
  probe.style.whiteSpace = "pre-wrap";
  probe.style.overflowWrap = "break-word";
  probe.style.wordBreak = "normal";
}

function applyProbeNowrap(probe: HTMLDivElement): void {
  probe.style.whiteSpace = "pre";
  probe.style.overflowWrap = "normal";
  probe.style.wordBreak = "normal";
  probe.style.width = "max-content";
  probe.style.maxWidth = "none";
}

/** Ширина без переноса (самая длинная явная строка). */
export function measureTextUnwrappedWidth(
  text: string,
  style: TextMeasureStyle,
  maxW = TEXT_BOX_UNWRAP_MAX_W
): number {
  const probe = getMeasureProbe();
  const fontSize = style.fontSize > 0 ? style.fontSize : 18;
  if (!probe) {
    const lines = (text || " ").split("\n");
    const longest = lines.reduce((m, l) => Math.max(m, l.length), 1);
    return Math.min(
      maxW,
      Math.max(TEXT_BOX_MIN_W, Math.round(longest * fontSize * 0.55) + TEXT_BOX_PAD_X * 2)
    );
  }
  applyProbeFont(probe, style);
  applyProbeNowrap(probe);
  probe.textContent = text.length ? text : " ";
  return Math.min(maxW, Math.max(TEXT_BOX_MIN_W, Math.ceil(probe.scrollWidth) + TEXT_WRAP_SLACK_PX));
}

/** Мировой размер текстового блока под содержимое (auto-режим, как при наборе). */
export function measureTextObjectSize(
  text: string,
  style: TextMeasureStyle,
  maxW = TEXT_BOX_MAX_W,
  /** Разметка кусков (часть текста крупнее/жирнее) — меряем по ней. */
  html?: string
): { w: number; h: number } {
  const probe = getMeasureProbe();
  const fontSize = style.fontSize > 0 ? style.fontSize : 18;
  const lineHeight = style.lineHeight > 0 ? style.lineHeight : 1.35;
  if (!probe) {
    const lines = (text || " ").split("\n");
    const longest = lines.reduce((m, l) => Math.max(m, l.length), 1);
    return {
      w: Math.min(maxW, Math.max(TEXT_BOX_MIN_W, Math.round(longest * fontSize * 0.55) + TEXT_BOX_PAD_X * 2)),
      h: Math.max(TEXT_BOX_MIN_H, Math.round(lines.length * fontSize * lineHeight) + TEXT_BOX_PAD_Y * 2),
    };
  }
  applyProbeFont(probe, style);
  applyProbeWrap(probe);
  probe.style.width = "max-content";
  probe.style.maxWidth = `${maxW}px`;
  if (html) probe.innerHTML = html;
  else probe.textContent = text.length ? text : " ";
  // scrollWidth + slack: offsetWidth часто ровно на грани и даёт ложный перенос после фиксации w.
  const rawW = Math.max(probe.scrollWidth, probe.offsetWidth);
  const w = Math.min(maxW, Math.max(TEXT_BOX_MIN_W, Math.ceil(rawW) + TEXT_WRAP_SLACK_PX));
  probe.style.width = `${w}px`;
  probe.style.maxWidth = `${w}px`;
  const h = Math.max(TEXT_BOX_MIN_H, Math.ceil(probe.offsetHeight) + underlineExtra(style));
  return { w, h };
}

/** Высота при фиксированной ширине (перенос строк). */
export function measureTextObjectSizeAtWidth(
  text: string,
  style: TextMeasureStyle,
  width: number,
  /** Разметка кусков (часть текста крупнее/жирнее) — меряем по ней. */
  html?: string
): { w: number; h: number } {
  const w = Math.max(TEXT_BOX_MIN_W, Math.round(width));
  const probe = getMeasureProbe();
  const fontSize = style.fontSize > 0 ? style.fontSize : 18;
  const lineHeight = style.lineHeight > 0 ? style.lineHeight : 1.35;
  if (!probe) {
    const lines = (text || " ").split("\n");
    const charsPerLine = Math.max(1, Math.floor((w - TEXT_BOX_PAD_X * 2) / (fontSize * 0.55)));
    const wrappedLines = lines.reduce(
      (sum, line) => sum + Math.max(1, Math.ceil(line.length / charsPerLine)),
      0
    );
    return {
      w,
      h: Math.max(TEXT_BOX_MIN_H, Math.round(wrappedLines * fontSize * lineHeight) + TEXT_BOX_PAD_Y * 2),
    };
  }
  applyProbeFont(probe, style);
  applyProbeWrap(probe);
  probe.style.width = `${w}px`;
  probe.style.maxWidth = `${w}px`;
  if (html) probe.innerHTML = html;
  else probe.textContent = text.length ? text : " ";
  const h = Math.max(TEXT_BOX_MIN_H, Math.ceil(probe.offsetHeight) + underlineExtra(style));
  return { w, h };
}

/** Miro: угол — один масштаб на рамку и кегль, без пересчёта переноса. */
export function scaleTextPatchUniform(
  objStart: Bounds,
  handle: ResizeHandleId,
  scale: number,
  startFontSize: number,
  altKey = false
): { x: number; y: number; w: number; h: number; fontSize: number } {
  const fontSize = scaledFontSize(startFontSize, scale);
  // Тот же коэффициент, что и у кегля — иначе из‑за ceil(w) слова прыгают со строки на строку.
  const ratio = startFontSize > 0 ? fontSize / startFontSize : scale;
  const w = Math.max(TEXT_BOX_MIN_W, Math.round(objStart.w * ratio * 10) / 10);
  const h = Math.max(TEXT_BOX_MIN_H, Math.round(objStart.h * ratio * 10) / 10);
  const startRight = objStart.x + objStart.w;
  const startBottom = objStart.y + objStart.h;
  const startCx = objStart.x + objStart.w / 2;
  const startCy = objStart.y + objStart.h / 2;
  let x: number;
  let y: number;
  if (altKey) {
    x = startCx - w / 2;
    y = startCy - h / 2;
  } else {
    x = handle.includes("w") ? startRight - w : objStart.x;
    y = handle.includes("n") ? startBottom - h : objStart.y;
  }
  return {
    x: Math.round(x * 10) / 10,
    y: Math.round(y * 10) / 10,
    w,
    h,
    fontSize,
  };
}

/**
 * Miro-ресайз:
 * - e/w — ширина + перенос, кегль тот же;
 * - угол — пропорции снаружи, здесь не трогаем перенос;
 * - n/s — только высота по содержимому при текущей ширине.
 */
export function clampTextLikePatchToContent(
  patch: { x: number; y: number; w: number; h: number },
  obj: SceneObject,
  handle: ResizeHandleId,
  objStart: Bounds,
  altKey = false
): { x: number; y: number; w: number; h: number } {
  if (!isTextLikeObject(obj)) return patch;

  const style = textDisplayStyle(obj);
  const text = textContent(obj);
  // Куски со своим размером или жирностью меряем по разметке: по общему
  // размеру рамка выходила в одну строку, а текст занимал три и вываливался.
  const objRunsNow = isTextObject(obj) ? objectRuns(obj) : null;
  const html = objRunsNow ? runsToHtml(objRunsNow) : undefined;
  const startRight = objStart.x + objStart.w;
  const startBottom = objStart.y + objStart.h;
  const startCx = objStart.x + objStart.w / 2;
  const startCy = objStart.y + objStart.h / 2;

  const placeX = (w: number, preferredX: number) => {
    if (altKey) return startCx - w / 2;
    if (handle.includes("w") && !handle.includes("e")) return startRight - w;
    if (handle.includes("e") && !handle.includes("w")) return objStart.x;
    if (Math.abs(w - patch.w) > 0.5) return startCx - w / 2;
    return preferredX;
  };
  const placeY = (h: number, preferredY: number) => {
    if (altKey) return startCy - h / 2;
    if (handle.includes("n") && !handle.includes("s")) return startBottom - h;
    if (handle.includes("s") && !handle.includes("n")) return objStart.y;
    if (Math.abs(h - patch.h) > 0.5) return startCy - h / 2;
    return preferredY;
  };

  if (isTextObject(obj) && handle.length === 2) {
    return {
      x: Math.round(patch.x),
      y: Math.round(patch.y),
      w: Math.max(TEXT_BOX_MIN_W, Math.round(patch.w)),
      h: Math.max(TEXT_BOX_MIN_H, Math.round(patch.h)),
    };
  }

  if (isTextObject(obj) && (handle === "e" || handle === "w")) {
    const w = Math.min(TEXT_BOX_UNWRAP_MAX_W, Math.max(TEXT_BOX_MIN_W, Math.round(patch.w)));
    const h = measureTextObjectSizeAtWidth(text, style, w, html).h;
    return {
      x: Math.round(placeX(w, patch.x)),
      y: Math.round(objStart.y),
      w: Math.round(w),
      h: Math.round(h),
    };
  }

  if (isTextObject(obj) && (handle === "n" || handle === "s")) {
    const w = Math.max(TEXT_BOX_MIN_W, Math.round(objStart.w));
    if (!text.trim() && textWidthMode(obj) === "fixed") {
      const h = Math.max(TEXT_BOX_MIN_H, Math.round(patch.h));
      return {
        x: Math.round(objStart.x),
        y: Math.round(placeY(h, patch.y)),
        w: Math.round(w),
        h: Math.round(h),
      };
    }
    const h = measureTextObjectSizeAtWidth(text, style, w, html).h;
    return {
      x: Math.round(objStart.x),
      y: Math.round(placeY(h, objStart.y)),
      w: Math.round(w),
      h: Math.round(h),
    };
  }

  const w = Math.max(TEXT_BOX_MIN_W, patch.w);
  const contentH = measureTextObjectSizeAtWidth(text, style, w, html).h;
  const h = Math.max(patch.h, contentH);
  if (w <= patch.w + 0.5 && h <= patch.h + 0.5) {
    return w === patch.w && h === patch.h ? patch : { ...patch, w, h };
  }
  return {
    x: Math.round(placeX(w, patch.x)),
    y: Math.round(placeY(h, patch.y)),
    w: Math.round(w),
    h: Math.round(h),
  };
}

/** Стиль + подгонка размера (auto = по содержимому, fixed = только высота). */
export function patchTextStyleAndFit(
  obj: SceneObject,
  stylePatch: Record<string, unknown>
): Record<string, unknown> {
  const prev =
    obj.style && typeof obj.style === "object"
      ? (obj.style as Record<string, unknown>)
      : {};
  const nextObj = { ...obj, style: { ...prev, ...stylePatch } } as SceneObject;
  const patch: Record<string, unknown> = { style: stylePatch };
  if (!isTextObject(obj)) return patch;
  const display = textDisplayStyle(nextObj);
  const text = textContent(obj);
  // У надписи с оформлением кусками размер считаем по разметке: иначе крупный
  // кусок не помещался бы в коробку.
  let runs = objectRuns(obj);
  // Размер всей надписи ложится на весь текст: свои размеры кусков снимаем.
  if ("fontSize" in stylePatch && runs && runs.some((r) => r.fontSize)) {
    const content = runsPatch(runsWithoutFontSize(runs));
    patch.runs = content.runs ?? null;
    runs = content.runs ?? null;
  }
  const html = runs ? runsToHtml(runs) : undefined;
  if (textWidthMode(obj) === "fixed") {
    const baseW = Math.max(TEXT_BOX_MIN_W, Math.round(Number(obj.w) || TEXT_BOX_MIN_W));
    const w = grownTextWidth(obj, text, display, baseW, html);
    if (w !== baseW) patch.w = w;
    patch.h = measureTextObjectSizeAtWidth(text, display, w, html).h;
  } else {
    const size = measureTextObjectSize(text, display, TEXT_BOX_MAX_W, html);
    patch.w = size.w;
    patch.h = size.h;
  }
  return patch;
}

/**
 * Новый текст надписи (кусками) и рамка под него — для правок с панели,
 * когда поле набора не открыто: список, маркер на всю надпись.
 */
export function patchTextRunsAndFit(obj: SceneObject, runs: TextRun[]): Record<string, unknown> {
  const content = runsPatch(runs);
  const patch: Record<string, unknown> = { text: content.text, runs: content.runs ?? null };
  if (!isTextObject(obj)) return patch;
  const display = textDisplayStyle(obj);
  const html = content.runs ? runsToHtml(content.runs) : undefined;
  if (textWidthMode(obj) === "fixed") {
    const baseW = Math.max(TEXT_BOX_MIN_W, Math.round(Number(obj.w) || TEXT_BOX_MIN_W));
    const w = grownTextWidth(obj, content.text, display, baseW, html);
    if (w !== baseW) patch.w = w;
    patch.h = measureTextObjectSizeAtWidth(content.text, display, w, html).h;
  } else {
    const size = measureTextObjectSize(content.text, display, TEXT_BOX_MAX_W, html);
    patch.w = size.w;
    patch.h = size.h;
  }
  return patch;
}
